/**
 * Proxy settings component
 * Manage proxy configurations
 */

import { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  Typography,
  Tooltip,
  Badge,
  Popconfirm,
  Statistic,
  Row,
  Col,
  Radio,
  Alert,
  Divider,
  Descriptions,
  message,
} from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type { RadioChangeEvent } from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  QuestionCircleOutlined,
  CloudDownloadOutlined,
  GlobalOutlined,
  ApiOutlined,
  WifiOutlined,
} from '@ant-design/icons';
import { ProxyTestButton } from './ProxyTestButton';
import { formatRelativeTime } from '@/utils/formatters';
import {
  getProxyConfig,
  updateProxyType,
  testResidentialProxy,
  type ProxyTypeConfig,
} from '@/services/systemApi';

const { Text, Title } = Typography;

/**
 * Proxy data from backend
 */
interface ProxyData {
  id: string;
  host: string;
  port: number;
  username?: string;
  password?: string;
  protocol: 'http' | 'https' | 'socks5';
  status: 'active' | 'inactive' | 'failed';
  lastChecked?: string;
  latency?: number;
  failureCount: number;
  createdAt: string;
  updatedAt: string;
}

interface ProxySettingsProps {
  proxies: ProxyData[];
  loading?: boolean;
  onAdd?: (proxy: AddProxyInput) => void;
  onDelete?: (id: string) => void;
  onTest?: (id: string) => Promise<boolean>;
  onTestAll?: () => void;
  onRefresh?: () => Promise<void>;
  refreshing?: boolean;
}

interface AddProxyInput {
  host: string;
  port: number;
  username?: string;
  password?: string;
  protocol: 'http' | 'https' | 'socks5';
}

export function ProxySettings({
  proxies,
  loading,
  onAdd,
  onDelete,
  onTest,
  onTestAll,
  onRefresh,
  refreshing,
}: ProxySettingsProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 50,
    showSizeChanger: true,
    pageSizeOptions: ['20', '50', '100', '200'],
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} proxies`,
  });

  // Proxy type configuration state
  const [proxyConfig, setProxyConfig] = useState<ProxyTypeConfig | null>(null);
  const [proxyTypeLoading, setProxyTypeLoading] = useState(false);
  const [residentialTestResult, setResidentialTestResult] = useState<{
    success?: boolean;
    ip?: string;
    country?: string;
    latency?: number;
    error?: string;
  } | null>(null);
  const [residentialTestLoading, setResidentialTestLoading] = useState(false);

  // Fetch proxy config on mount
  useEffect(() => {
    fetchProxyConfig();
  }, []);

  const fetchProxyConfig = async () => {
    try {
      const config = await getProxyConfig();
      setProxyConfig(config);
    } catch (err) {
      console.error('Failed to fetch proxy config:', err);
    }
  };

  const handleProxyTypeChange = async (e: RadioChangeEvent) => {
    const newType = e.target.value as 'premium' | 'residential';
    setProxyTypeLoading(true);
    try {
      await updateProxyType(newType);
      setProxyConfig(prev => prev ? { ...prev, proxyType: newType } : null);
      message.success(`Switched to ${newType} proxy mode`);
    } catch (err: unknown) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to change proxy type';
      message.error(errorMsg);
    } finally {
      setProxyTypeLoading(false);
    }
  };

  const handleTestResidential = async () => {
    setResidentialTestLoading(true);
    setResidentialTestResult(null);
    try {
      const result = await testResidentialProxy();
      setResidentialTestResult(result);
      if (result.success) {
        message.success('Residential proxy is working!');
      } else {
        message.error(result.error || 'Residential proxy test failed');
      }
    } catch (err: unknown) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to test residential proxy';
      message.error(errorMsg);
      setResidentialTestResult({ success: false, error: errorMsg });
    } finally {
      setResidentialTestLoading(false);
    }
  };

  const handleAdd = () => {
    form.validateFields().then((values) => {
      // Parse host:port format if provided as URL
      let host = values.host;
      let port = values.port;

      if (host && host.includes(':') && !port) {
        const parts = host.split(':');
        host = parts[0];
        port = parseInt(parts[1], 10);
      }

      onAdd?.({ ...values, host, port: port || 8080 });
      form.resetFields();
      setModalOpen(false);
    });
  };

  const handleTableChange = (newPagination: TablePaginationConfig) => {
    setPagination(newPagination);
  };

  /**
   * Get status display based on proxy data
   */
  const getStatusDisplay = (record: ProxyData) => {
    if (!record.lastChecked) {
      return {
        status: 'default' as const,
        text: 'Untested',
        icon: <QuestionCircleOutlined />,
      };
    }
    if (record.status === 'active') {
      return {
        status: 'success' as const,
        text: 'Active',
        icon: <CheckCircleOutlined />,
      };
    }
    if (record.status === 'failed') {
      return {
        status: 'error' as const,
        text: 'Failed',
        icon: <CloseCircleOutlined />,
      };
    }
    return {
      status: 'warning' as const,
      text: 'Inactive',
      icon: <QuestionCircleOutlined />,
    };
  };

  const columns: ColumnsType<ProxyData> = [
    {
      title: 'Status',
      key: 'status',
      width: 100,
      align: 'center',
      render: (_, record) => {
        const { status, text } = getStatusDisplay(record);
        return <Badge status={status} text={text} />;
      },
    },
    {
      title: 'Proxy Address',
      key: 'address',
      ellipsis: true,
      render: (_, record) => (
        <Text copyable={{ text: `${record.host}:${record.port}` }} style={{ fontSize: 13 }}>
          {record.host}:{record.port}
        </Text>
      ),
    },
    {
      title: 'Protocol',
      dataIndex: 'protocol',
      key: 'protocol',
      width: 90,
      render: (protocol: string) => (
        <Tag>{protocol?.toUpperCase() || 'HTTP'}</Tag>
      ),
    },
    {
      title: 'Latency',
      key: 'latency',
      width: 100,
      render: (_, record) =>
        record.latency ? (
          <Text type={record.latency > 2000 ? 'warning' : undefined}>
            {record.latency}ms
          </Text>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: 'Last Checked',
      key: 'lastChecked',
      width: 130,
      render: (_, record) =>
        record.lastChecked ? (
          <Tooltip title={new Date(record.lastChecked).toLocaleString()}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {formatRelativeTime(record.lastChecked)}
            </Text>
          </Tooltip>
        ) : (
          <Text type="secondary">Never</Text>
        ),
    },
    {
      title: 'Failures',
      dataIndex: 'failureCount',
      key: 'failureCount',
      width: 80,
      align: 'center',
      render: (count: number = 0) => (
        <Text type={count > 0 ? 'danger' : 'secondary'}>
          {count}
        </Text>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Space size={4}>
          {onTest && (
            <ProxyTestButton
              onTest={() => onTest(record.id)}
              size="small"
            />
          )}
          {onDelete && !record.id.startsWith('api-') && (
            <Popconfirm
              title="Delete this proxy?"
              onConfirm={() => onDelete(record.id)}
            >
              <Button
                type="text"
                size="small"
                icon={<DeleteOutlined />}
                danger
              />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  // Calculate stats
  const activeCount = proxies.filter((p) => p.status === 'active').length;
  const failedCount = proxies.filter((p) => p.status === 'failed').length;
  const untestedCount = proxies.filter((p) => !p.lastChecked).length;

  return (
    <div>
      {/* Proxy Type Selector */}
      <Card style={{ marginBottom: 16 }}>
        <Title level={5} style={{ marginTop: 0, marginBottom: 16 }}>
          <GlobalOutlined style={{ marginRight: 8 }} />
          Proxy Mode
        </Title>

        <Radio.Group
          value={proxyConfig?.proxyType || 'premium'}
          onChange={handleProxyTypeChange}
          disabled={proxyTypeLoading}
          style={{ marginBottom: 16 }}
        >
          <Radio.Button value="premium" style={{ minWidth: 180 }}>
            <ApiOutlined style={{ marginRight: 8 }} />
            Premium Proxies
          </Radio.Button>
          <Radio.Button
            value="residential"
            style={{ minWidth: 180 }}
            disabled={!proxyConfig?.residential.isConfigured}
          >
            <WifiOutlined style={{ marginRight: 8 }} />
            Residential Gateway
          </Radio.Button>
        </Radio.Group>

        {proxyConfig?.proxyType === 'premium' && (
          <Alert
            type="info"
            showIcon
            message="Premium Proxy Mode"
            description="Using rotating premium proxies fetched from ProxyScrape. Each request uses a different proxy from the pool below."
            style={{ marginBottom: 0 }}
          />
        )}

        {proxyConfig?.proxyType === 'residential' && (
          <div>
            <Alert
              type="success"
              showIcon
              message="Residential Gateway Mode"
              description="Using a single residential proxy gateway. All requests go through one gateway that rotates IPs automatically. Billed by bandwidth (GB) usage."
              style={{ marginBottom: 16 }}
            />

            <Descriptions bordered size="small" column={2} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Gateway Host">
                {proxyConfig.residential.host}
              </Descriptions.Item>
              <Descriptions.Item label="Port">
                {proxyConfig.residential.port}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                {proxyConfig.residential.isConfigured ? (
                  <Badge status="success" text="Configured" />
                ) : (
                  <Badge status="error" text="Not Configured" />
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Protocol">HTTP</Descriptions.Item>
            </Descriptions>

            <Space>
              <Button
                type="primary"
                icon={<SyncOutlined />}
                loading={residentialTestLoading}
                onClick={handleTestResidential}
              >
                Test Connection
              </Button>
            </Space>

            {residentialTestResult && (
              <div style={{ marginTop: 16 }}>
                {residentialTestResult.success ? (
                  <Alert
                    type="success"
                    message="Connection Successful"
                    description={
                      <div>
                        <div><strong>IP:</strong> {residentialTestResult.ip}</div>
                        <div><strong>Country:</strong> {residentialTestResult.country}</div>
                        <div><strong>Latency:</strong> {residentialTestResult.latency}ms</div>
                      </div>
                    }
                  />
                ) : (
                  <Alert
                    type="error"
                    message="Connection Failed"
                    description={residentialTestResult.error}
                  />
                )}
              </div>
            )}
          </div>
        )}

        {!proxyConfig?.residential.isConfigured && proxyConfig?.proxyType !== 'residential' && (
          <Alert
            type="warning"
            showIcon
            message="Residential Proxy Not Configured"
            description="Set RESIDENTIAL_PROXY_USERNAME and RESIDENTIAL_PROXY_PASSWORD in your .env file to enable residential proxy mode."
            style={{ marginTop: 16 }}
          />
        )}
      </Card>

      {/* Premium Proxies Section - only show when in premium mode */}
      {proxyConfig?.proxyType === 'premium' && (
        <Card
          title={
            <Space>
              <span>Premium Proxy Pool</span>
              <Tag color="blue">{proxies.length} proxies</Tag>
            </Space>
          }
          extra={
            <Space>
              {onRefresh && (
                <Tooltip title="Fetch fresh proxies from ProxyScrape API">
                  <Button
                    icon={<CloudDownloadOutlined />}
                    onClick={onRefresh}
                    loading={refreshing}
                  >
                    Refresh from ProxyScrape
                  </Button>
                </Tooltip>
              )}
              {onTestAll && (
                <Button
                  icon={<SyncOutlined />}
                  onClick={onTestAll}
                  loading={loading}
                >
                  Test All
                </Button>
              )}
              {onAdd && (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setModalOpen(true)}
                >
                  Add Proxy
                </Button>
              )}
            </Space>
          }
        >
          {/* Stats row */}
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={6}>
              <Statistic
                title="Total Proxies"
                value={proxies.length}
                valueStyle={{ fontSize: 20 }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Active"
                value={activeCount}
                valueStyle={{ fontSize: 20, color: '#52c41a' }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Failed"
                value={failedCount}
                valueStyle={{ fontSize: 20, color: failedCount > 0 ? '#ff4d4f' : undefined }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Untested"
                value={untestedCount}
                valueStyle={{ fontSize: 20, color: '#999' }}
              />
            </Col>
          </Row>

          <Table
            columns={columns}
            dataSource={proxies}
            rowKey="id"
            loading={loading}
            pagination={pagination}
            onChange={handleTableChange}
            size="small"
            scroll={{ x: 800 }}
            locale={{ emptyText: 'No proxies configured' }}
          />
        </Card>
      )}

      {/* Add proxy modal */}
      <Modal
        title="Add Proxy"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleAdd}
        okText="Add"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="host"
            label="Proxy Host"
            rules={[{ required: true, message: 'Please enter proxy host' }]}
            help="Format: host:port (e.g., proxy.example.com:8080)"
          >
            <Input placeholder="proxy.example.com:8080" />
          </Form.Item>

          <Form.Item name="port" label="Port (optional if included in host)">
            <Input type="number" placeholder="8080" />
          </Form.Item>

          <Form.Item name="protocol" label="Protocol" initialValue="http">
            <Select
              options={[
                { value: 'http', label: 'HTTP' },
                { value: 'https', label: 'HTTPS' },
                { value: 'socks5', label: 'SOCKS5' },
              ]}
            />
          </Form.Item>

          <Form.Item name="username" label="Username (optional)">
            <Input placeholder="username" />
          </Form.Item>

          <Form.Item name="password" label="Password (optional)">
            <Input.Password placeholder="password" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default ProxySettings;
